/**
 * RBAC Service — Roles, Groups & Access Control
 *
 * Inspired by ServiceNow IAM model:
 * - Roles define capabilities
 * - Groups contain members and inherit roles
 * - ACL rules map roles to module/subModule CRUD permissions
 * - super_admin bypasses all ACL checks
 */

import { Injectable } from '@angular/core';
// #1106 — le CALCUL du plan est une fonction pure, testée hors de ce service.
import {
  AclReconciliationPlan, AclReconciliationResult, computeAclReconciliationPlan,
} from '../models/acl-seed-reconciliation';
import {
  Firestore, collection, query, where, getDocs, doc, updateDoc,
  getDoc, addDoc, deleteDoc, orderBy, arrayUnion, arrayRemove, setDoc, writeBatch
} from '@angular/fire/firestore';
import { Functions, httpsCallable } from '@angular/fire/functions';
import { Auth } from '@angular/fire/auth';
import { BehaviorSubject, Observable } from 'rxjs';
import {
  RbacRole, RbacGroup, RbacAclRule, AclOperations, EffectivePermissions,
  RbacModule, CrudOperation, EMPTY_ACL_OPS, FULL_ACL_OPS, SYSTEM_ROLES, SEED_GROUPS,
  RBAC_SUBMODULES, AssignEmployeeRoleResult, PropagateRbacClaimsResult
} from '../models/rbac.models';
import { MODULE_ROUTE_ROLES, SUBMODULE_SEED_ROLES } from '../models/role-sets';

@Injectable({ providedIn: 'root' })
export class RbacService {

  private permissionsSubject = new BehaviorSubject<EffectivePermissions | null>(null);
  permissions$ = this.permissionsSubject.asObservable();

  private currentUid = '';
  private loaded = false;

  /**
   * #812 — modules ayant AU MOINS une règle ACL active dans rbac_acl (toutes
   * rôles confondus). Sert au fallback legacy PAR module de PermissionService :
   * un module sans aucune règle n'est pas « couvert » par la matrice, et une
   * matrice incomplète ne doit jamais produire de lockout. Vide tant que
   * loadCurrentUserPermissions n'a pas abouti.
   */
  private coveredModules = new Set<RbacModule>();

  constructor(
    private firestore: Firestore,
    private functions: Functions,
    private auth: Auth,
  ) {}

  // ══════════════════════════════════════════
  // #814 — ÉCRITURES PRIVILÉGIÉES : CLOUD FUNCTIONS UNIQUEMENT
  // ══════════════════════════════════════════
  //
  // Le rôle et les groupes d'un employé déterminent ses custom claims, donc ce
  // que les firestore.rules l'autorisent à faire. Les écrire depuis le client
  // laisserait les claims en retard sur Firestore (et l'audit sans trace) :
  // ces deux chemins passent par des callables admin-only qui écrivent,
  // recalculent les claims et journalisent dans `rbac_audit`.

  /**
   * Assigne rôle / rôles directs / groupes à un employé (CF `assignEmployeeRole`).
   *
   * Ne fournir que les champs réellement modifiés : la CF n'écrit que ceux
   * qu'elle reçoit, et un appel sans aucun d'eux est rejeté. Elle synchronise
   * elle-même les deux côtés du lien `employees.groupIds` ↔ `rbac_groups.memberIds`
   * (inutile d'appeler `addMemberToGroup`/`removeMemberFromGroup` en plus).
   *
   * ⚠️ admin-only côté serveur — n'appeler que si `PermissionService.canEditRole()`.
   */
  async assignEmployeeRole(input: {
    employeeId: string;
    role?: string;
    directRoleIds?: string[];
    groupIds?: string[];
    reason?: string;
  }): Promise<AssignEmployeeRoleResult> {
    const callable = httpsCallable<typeof input, AssignEmployeeRoleResult>(
      this.functions, 'assignEmployeeRole'
    );
    const res = await callable(input);
    return res.data;
  }

  /**
   * Recalcule les custom claims de TOUS les employés (CF `propagateRbacClaims`).
   *
   * À lancer après une modification de la matrice ACL, des rôles ou des groupes :
   * ces objets ne portent pas de claims, ce sont les employés qui en héritent.
   * Sert aussi de **kill-switch** : flag RBAC repassé à `false` puis propagation
   * ⇒ les claims portent `rbacOn: false` et les branches « matrice » des rules
   * s'éteignent, sans redéploiement.
   */
  async propagateClaims(dryRun = false): Promise<PropagateRbacClaimsResult> {
    const callable = httpsCallable<{ dryRun: boolean }, PropagateRbacClaimsResult>(
      this.functions, 'propagateRbacClaims'
    );
    const res = await callable({ dryRun });
    return res.data;
  }

  /**
   * Force le rafraîchissement de l'ID token de l'utilisateur COURANT.
   *
   * Les custom claims posées côté serveur ne parviennent au client (et aux
   * firestore.rules évaluées pour ses requêtes) qu'au renouvellement du token —
   * sinon jusqu'à une heure de décalage. À appeler après une propagation pour
   * que l'admin constate immédiatement l'effet de ce qu'il vient de changer.
   */
  async refreshCurrentUserToken(): Promise<void> {
    await this.auth.currentUser?.getIdToken(true);
  }

  // ══════════════════════════════════════════
  // ROLES CRUD
  // ══════════════════════════════════════════

  async getRoles(): Promise<RbacRole[]> {
    const ref = collection(this.firestore, 'rbac_roles');
    const q = query(ref, orderBy('name', 'asc'));
    const snap = await getDocs(q);
    return snap.docs.map(d => this.mapRole(d.id, d.data()));
  }

  async getRoleById(id: string): Promise<RbacRole | null> {
    const ref = doc(this.firestore, 'rbac_roles', id);
    const snap = await getDoc(ref);
    if (!snap.exists()) return null;
    return this.mapRole(snap.id, snap.data());
  }

  async createRole(data: { name: string; label: string; description: string }): Promise<string> {
    const ref = collection(this.firestore, 'rbac_roles');
    const now = new Date();
    const docRef = await addDoc(ref, {
      name: data.name,
      label: data.label,
      description: data.description,
      isSystem: false,
      createdAt: now,
      updatedAt: now,
    });
    return docRef.id;
  }

  async updateRole(id: string, data: Partial<RbacRole>): Promise<void> {
    const ref = doc(this.firestore, 'rbac_roles', id);
    const { id: _, isSystem, createdAt, ...updateData } = data as any;
    await updateDoc(ref, { ...updateData, updatedAt: new Date() });
  }

  async deleteRole(id: string): Promise<void> {
    // Check if system role
    const role = await this.getRoleById(id);
    if (role?.isSystem) throw new Error('Cannot delete system role');
    await deleteDoc(doc(this.firestore, 'rbac_roles', id));
  }

  /** Seed system roles if they don't exist */
  async seedSystemRoles(): Promise<number> {
    const existing = await this.getRoles();
    const existingNames = new Set(existing.map(r => r.name));
    let created = 0;

    for (const sr of SYSTEM_ROLES) {
      if (!existingNames.has(sr.name)) {
        const ref = collection(this.firestore, 'rbac_roles');
        await addDoc(ref, {
          name: sr.name,
          label: sr.label,
          description: sr.description,
          isSystem: true,
          createdAt: new Date(),
          updatedAt: new Date(),
        });
        created++;
      }
    }
    return created;
  }

  /** Seed default groups — only creates groups whose name doesn't already exist (two-pass: parents first, then children) */
  async seedGroups(): Promise<number> {
    const existing = await this.getGroups();
    const existingNames = new Set(existing.map(g => g.name));

    // Build name→id map from existing groups (for parent resolution)
    const nameToId = new Map<string, string>();
    existing.forEach(g => nameToId.set(g.name, g.id));

    const newSeeds = SEED_GROUPS.filter(g => !existingNames.has(g.name));
    if (newSeeds.length === 0) return 0;

    // Pass 1: create parent groups (no parentName)
    const parents = newSeeds.filter(g => !g.parentName);
    const children = newSeeds.filter(g => !!g.parentName);

    for (const g of parents) {
      const id = await this.createGroup({
        name: g.name, description: g.description,
        location: g.location, department: g.department,
      });
      nameToId.set(g.name, id);
    }

    // Pass 2: create child groups with resolved parentGroupId
    for (const g of children) {
      const parentId = g.parentName ? nameToId.get(g.parentName) : undefined;
      const id = await this.createGroup({
        name: g.name, description: g.description,
        location: g.location, department: g.department,
        parentGroupId: parentId,
      });
      nameToId.set(g.name, id);
    }

    return parents.length + children.length;
  }

  /**
   * #813 — seed des règles ACL par défaut depuis le référentiel legacy
   * (role-sets.ts, le même qui pilote les data.roles des routes et le menu).
   *
   * Pour chaque module SANS AUCUNE règle existante (on ne touche jamais à une
   * matrice déjà configurée), crée une règle FULL par sous-module × rôle du
   * set legacy du module. Point de DÉPART à affiner ensuite dans la matrice —
   * les écrans legacy donnaient aux rôles opérationnels les actions de leurs
   * écrans (ex. warehouse crée des groupages), d'où FULL plutôt que read-only.
   * Le module 'admin' est exclu : jamais délégué via la matrice.
   *
   * Exceptions par sous-module : `SUBMODULE_SEED_ROLES` (#935). Un sous-module
   * dont la route est plus restrictive que l'union de son module s'y déclare,
   * sinon la bascule du drapeau (#815) l'élargit en silence.
   *
   * @returns nombre de règles créées
   */
  async seedLegacyAclRules(): Promise<number> {
    const roles = await this.getRoles();
    const roleIdByName = new Map(roles.map(r => [r.name, r.id]));
    const existing = await this.getAclRules();
    const modulesWithRules = new Set(existing.map(r => r.module));

    const now = new Date();
    const colRef = collection(this.firestore, 'rbac_acl');
    let batch = writeBatch(this.firestore);
    let opsInBatch = 0;
    let created = 0;

    for (const [module, subModules] of Object.entries(RBAC_SUBMODULES)) {
      if (module === 'admin') continue;
      if (modulesWithRules.has(module as RbacModule)) continue;

      for (const sub of subModules) {
        const roleNames = SUBMODULE_SEED_ROLES[sub.key]
          ?? MODULE_ROUTE_ROLES[module as RbacModule] ?? [];
        for (const name of roleNames) {
          const roleId = roleIdByName.get(name);
          if (!roleId) continue; // rôle système pas encore seedé
          batch.set(doc(colRef), {
            module,
            subModule: sub.key,
            roleId,
            operations: { ...FULL_ACL_OPS },
            isActive: true,
            createdAt: now,
            updatedAt: now,
          });
          created++;
          opsInBatch++;
          if (opsInBatch >= 450) {
            await batch.commit();
            batch = writeBatch(this.firestore);
            opsInBatch = 0;
          }
        }
      }
    }

    if (opsInBatch > 0) await batch.commit();
    return created;
  }

  /**
   * #1106 — Calcule le plan de réconciliation de la matrice avec le seed.
   *
   * Lecture seule. Le CALCUL est une fonction pure testée à part ; ce wrapper
   * ne fait que lui fournir l'état réel (toutes les règles, tous les rôles).
   */
  async computeAclReconciliation(): Promise<AclReconciliationPlan> {
    const [rules, roles] = await Promise.all([this.getAclRules(), this.getRoles()]);
    return computeAclReconciliationPlan(rules, roles);
  }

  /**
   * #1106 — Applique un plan de réconciliation via la CF `reconcileAclMatrix`.
   *
   * Ce chemin existe parce que `seedLegacyAclRules` ci-dessus saute tout module
   * ayant déjà des règles : une correction du seed n'atteint jamais un
   * environnement déjà seedé. C'est ce qui a obligé à réconcilier dev à la main
   * en REST le 2026-08-04.
   *
   * POURQUOI UNE CF alors que le seed, lui, écrit depuis le client : le ticket
   * exige un journal « qui, quoi, avant/après », et `rbac_audit` est en
   * `allow write: if false` — délibérément, pour que la piste d'audit ne soit
   * pas forgeable par celui-là même qu'elle trace. Un chemin client ne peut
   * donc pas journaliser. La CF apporte en plus la **garde de plan périmé** :
   * elle relit chaque document visé et ignore ceux dont l'état ne correspond
   * plus à la prémisse du plan (un autre admin a pu écrire entre l'aperçu et la
   * confirmation).
   *
   * ⚠️ On DÉSACTIVE, on ne supprime jamais. `isActive: false` est réversible et
   * déjà respecté par les deux chargeurs — celui d'ici et `rbac-claims.ts` côté
   * serveur. Les orphelines et les doublons ne sont pas envoyés : le plan les
   * isole précisément pour qu'on n'y touche pas.
   *
   * ⚠️ Sans effet sur les droits tant que les claims ne sont pas propagées :
   * `firestore.rules` lit `rbacMods`/`rbacCov`, pas la matrice.
   */
  async applyAclReconciliation(
    plan: AclReconciliationPlan,
    dryRun = false,
  ): Promise<AclReconciliationResult> {
    const roles = await this.getRoles();
    const roleIdByName = new Map(roles.map(r => [r.name, r.id]));

    const input = {
      // Le seed écrit un id résolu depuis `rbac_roles` ; si le rôle n'existe pas
      // encore (« Créer rôles système » pas encore lancé), on transmet le nom :
      // la CF accepte les deux formes et refuse ce qu'elle ne résout pas.
      toCreate: plan.toCreate.map(c => ({
        module: c.module,
        subModule: c.subModule,
        roleId: roleIdByName.get(c.roleName) ?? c.roleName,
      })),
      toDeactivateIds: plan.toDeactivate.map(r => r.id),
      toReactivateIds: plan.toReactivate.map(r => r.id),
      dryRun,
    };

    const callable = httpsCallable<typeof input, AclReconciliationResult>(
      this.functions, 'reconcileAclMatrix'
    );
    const res = await callable(input);
    return res.data;
  }

  // ══════════════════════════════════════════
  // GROUPS CRUD
  // ══════════════════════════════════════════

  async getGroups(): Promise<RbacGroup[]> {
    const ref = collection(this.firestore, 'rbac_groups');
    const q = query(ref, orderBy('name', 'asc'));
    const snap = await getDocs(q);
    return snap.docs.map(d => this.mapGroup(d.id, d.data()));
  }

  async getGroupById(id: string): Promise<RbacGroup | null> {
    const ref = doc(this.firestore, 'rbac_groups', id);
    const snap = await getDoc(ref);
    if (!snap.exists()) return null;
    return this.mapGroup(snap.id, snap.data());
  }

  async createGroup(data: {
    name: string; description: string; location?: string;
    department?: string; managerId?: string; managerName?: string;
    parentGroupId?: string;
  }): Promise<string> {
    const ref = collection(this.firestore, 'rbac_groups');
    const now = new Date();
    const docData: Record<string, any> = {
      name: data.name,
      description: data.description,
      memberIds: [],
      roleIds: [],
      managerId: data.managerId || '',
      managerName: data.managerName || '',
      location: data.location || '',
      isActive: true,
      createdAt: now,
      updatedAt: now,
    };
    if (data.department) docData['department'] = data.department;
    if (data.parentGroupId) docData['parentGroupId'] = data.parentGroupId;
    const docRef = await addDoc(ref, docData);
    return docRef.id;
  }

  async updateGroup(id: string, data: Partial<RbacGroup>): Promise<void> {
    const ref = doc(this.firestore, 'rbac_groups', id);
    const { id: _, createdAt, ...updateData } = data as any;
    await updateDoc(ref, { ...updateData, updatedAt: new Date() });
  }

  async deleteGroup(id: string): Promise<void> {
    // Remove groupId from all members
    const group = await this.getGroupById(id);
    if (group) {
      for (const memberId of group.memberIds) {
        try {
          const empRef = doc(this.firestore, 'employees', memberId);
          await updateDoc(empRef, { groupIds: arrayRemove(id) });
        } catch {
          // Employé introuvable ou write refusé : on continue le retrait des autres
          // membres, le groupe est supprimé juste après de toute façon.
        }
      }
    }
    await deleteDoc(doc(this.firestore, 'rbac_groups', id));
  }

  async addMemberToGroup(groupId: string, employeeId: string): Promise<void> {
    const groupRef = doc(this.firestore, 'rbac_groups', groupId);
    const empRef = doc(this.firestore, 'employees', employeeId);
    await updateDoc(groupRef, { memberIds: arrayUnion(employeeId), updatedAt: new Date() });
    await updateDoc(empRef, { groupIds: arrayUnion(groupId) });
  }

  async removeMemberFromGroup(groupId: string, employeeId: string): Promise<void> {
    const groupRef = doc(this.firestore, 'rbac_groups', groupId);
    const empRef = doc(this.firestore, 'employees', employeeId);
    await updateDoc(groupRef, { memberIds: arrayRemove(employeeId), updatedAt: new Date() });
    await updateDoc(empRef, { groupIds: arrayRemove(groupId) });
  }

  async assignRoleToGroup(groupId: string, roleId: string): Promise<void> {
    const ref = doc(this.firestore, 'rbac_groups', groupId);
    await updateDoc(ref, { roleIds: arrayUnion(roleId), updatedAt: new Date() });
  }

  async removeRoleFromGroup(groupId: string, roleId: string): Promise<void> {
    const ref = doc(this.firestore, 'rbac_groups', groupId);
    await updateDoc(ref, { roleIds: arrayRemove(roleId), updatedAt: new Date() });
  }

  // ══════════════════════════════════════════
  // ACL RULES CRUD
  // ══════════════════════════════════════════

  async getAclRules(): Promise<RbacAclRule[]> {
    const ref = collection(this.firestore, 'rbac_acl');
    const snap = await getDocs(ref);
    return snap.docs.map(d => this.mapAcl(d.id, d.data()));
  }

  async getAclRulesForModule(module: RbacModule): Promise<RbacAclRule[]> {
    const ref = collection(this.firestore, 'rbac_acl');
    const q = query(ref, where('module', '==', module));
    const snap = await getDocs(q);
    return snap.docs.map(d => this.mapAcl(d.id, d.data()));
  }

  async saveAclRule(data: {
    module: RbacModule; subModule: string; roleId: string; operations: AclOperations;
  }): Promise<string> {
    // Upsert: check if rule already exists for this role+module+subModule
    const ref = collection(this.firestore, 'rbac_acl');
    const q = query(ref,
      where('module', '==', data.module),
      where('subModule', '==', data.subModule),
      where('roleId', '==', data.roleId)
    );
    const snap = await getDocs(q);
    const now = new Date();

    if (!snap.empty) {
      // Update existing
      const existingId = snap.docs[0].id;
      await updateDoc(doc(this.firestore, 'rbac_acl', existingId), {
        operations: data.operations,
        isActive: true,
        updatedAt: now,
      });
      return existingId;
    } else {
      // Create new
      const docRef = await addDoc(ref, {
        module: data.module,
        subModule: data.subModule,
        roleId: data.roleId,
        operations: data.operations,
        isActive: true,
        createdAt: now,
        updatedAt: now,
      });
      return docRef.id;
    }
  }

  async deleteAclRule(id: string): Promise<void> {
    await deleteDoc(doc(this.firestore, 'rbac_acl', id));
  }

  /**
   * Bulk save ACL matrix for a module (all roles × subModules).
   * #533 — writeBatch au lieu de N upserts séquentiels : 1 lecture des règles
   * existantes du module, puis commits par paquets de 450 (limite Firestore
   * = 500 writes par batch).
   */
  async saveAclMatrix(module: RbacModule, matrix: Record<string, Record<string, AclOperations>>): Promise<void> {
    // matrix[roleId][subModule] = { read, create, update, delete }
    const existingRules = await this.getAclRulesForModule(module);
    const existingIdByKey = new Map<string, string>();
    for (const rule of existingRules) {
      existingIdByKey.set(`${rule.roleId}|${rule.subModule}`, rule.id);
    }

    const now = new Date();
    const colRef = collection(this.firestore, 'rbac_acl');
    let batch = writeBatch(this.firestore);
    let opsInBatch = 0;

    for (const roleId of Object.keys(matrix)) {
      for (const subModule of Object.keys(matrix[roleId])) {
        const operations = matrix[roleId][subModule];
        const existingId = existingIdByKey.get(`${roleId}|${subModule}`);
        if (existingId) {
          batch.update(doc(this.firestore, 'rbac_acl', existingId), {
            operations,
            isActive: true,
            updatedAt: now,
          });
        } else {
          batch.set(doc(colRef), {
            module,
            subModule,
            roleId,
            operations,
            isActive: true,
            createdAt: now,
            updatedAt: now,
          });
        }
        opsInBatch++;
        if (opsInBatch >= 450) {
          await batch.commit();
          batch = writeBatch(this.firestore);
          opsInBatch = 0;
        }
      }
    }

    if (opsInBatch > 0) await batch.commit();
  }

  // ══════════════════════════════════════════
  // PERMISSION EVALUATION
  // ══════════════════════════════════════════

  /**
   * Load current user's effective permissions.
   * Called once after auth, cached in BehaviorSubject.
   */
  async loadCurrentUserPermissions(uid: string): Promise<void> {
    this.currentUid = uid;

    try {
      // 1. Get employee profile
      const empRef = doc(this.firestore, 'employees', uid);
      const empSnap = await getDoc(empRef);
      if (!empSnap.exists()) {
        this.coveredModules = new Set();
        this.permissionsSubject.next({ roles: [], groups: [], acl: new Map() });
        this.loaded = true;
        return;
      }

      const emp = empSnap.data();
      const primaryRole = emp['role'] || '';
      const directRoleIds: string[] = emp['directRoleIds'] || [];
      const groupIds: string[] = emp['groupIds'] || [];

      // 2. Resolve group-inherited roles (including parent group hierarchy)
      const groupRoles: string[] = [];
      const allGroups = await this.getGroups();
      const groupMap = new Map(allGroups.map(g => [g.id, g]));

      for (const gid of groupIds) {
        // Walk up the parent chain collecting roles
        let current = groupMap.get(gid);
        const visited = new Set<string>();
        while (current && current.isActive && !visited.has(current.id)) {
          visited.add(current.id);
          groupRoles.push(...current.roleIds);
          if (current.parentGroupId) {
            current = groupMap.get(current.parentGroupId);
          } else {
            break;
          }
        }
      }

      // 3. Union all roles (using role names, not IDs)
      const allRoleNames = new Set<string>();
      allRoleNames.add(primaryRole);
      // Resolve role IDs to names
      const allRoles = await this.getRoles();
      const roleIdToName = new Map(allRoles.map(r => [r.id, r.name]));
      const roleNameToId = new Map(allRoles.map(r => [r.name, r.id]));

      for (const rid of [...directRoleIds, ...groupRoles]) {
        const name = roleIdToName.get(rid);
        if (name) allRoleNames.add(name);
        else allRoleNames.add(rid); // fallback: treat as name
      }

      // 4. Load ACL rules
      const aclRules = await this.getAclRules();
      this.coveredModules = new Set(aclRules.filter(r => r.isActive).map(r => r.module));

      // 5. Build effective ACL map
      const acl = new Map<string, AclOperations>();

      // Collect all role IDs that match our effective role names
      const effectiveRoleIds = new Set<string>();
      for (const rn of allRoleNames) {
        const rid = roleNameToId.get(rn);
        if (rid) effectiveRoleIds.add(rid);
        effectiveRoleIds.add(rn); // also match by name directly
      }

      for (const rule of aclRules) {
        if (!rule.isActive) continue;
        // Check if this rule's roleId matches any of our effective roles
        const roleName = roleIdToName.get(rule.roleId) || rule.roleId;
        if (!effectiveRoleIds.has(rule.roleId) && !allRoleNames.has(roleName)) continue;

        const key = `${rule.module}:${rule.subModule}`;
        const existing = acl.get(key) || { ...EMPTY_ACL_OPS };

        // Merge: if any matching role grants an operation, it's granted
        acl.set(key, {
          read: existing.read || rule.operations.read,
          create: existing.create || rule.operations.create,
          update: existing.update || rule.operations.update,
          delete: existing.delete || rule.operations.delete,
        });
      }

      this.permissionsSubject.next({
        roles: Array.from(allRoleNames),
        groups: groupIds,
        acl,
      });
      this.loaded = true;
    } catch (err) {
      console.error('[RbacService] Error loading permissions:', err);
      this.coveredModules = new Set();
      this.permissionsSubject.next({ roles: [], groups: [], acl: new Map() });
      this.loaded = true;
    }
  }

  /** #812 — true si le module a au moins une règle ACL active (matrice « couvrante »). */
  isModuleCovered(module: RbacModule): boolean {
    return this.coveredModules.has(module);
  }

  /** Reload permissions (after role/group changes) */
  async refreshPermissions(): Promise<void> {
    if (this.currentUid) {
      await this.loadCurrentUserPermissions(this.currentUid);
    }
  }

  /**
   * Check if current user has permission for a module/subModule/operation.
   * super_admin always returns true.
   */
  can(module: RbacModule, subModule: string, operation: CrudOperation): boolean {
    const perms = this.permissionsSubject.value;
    if (!perms) return false;

    // super_admin bypass
    if (perms.roles.includes('super_admin')) return true;
    // admin gets full access by default
    if (perms.roles.includes('admin')) return true;
    // topManager gets full access by default
    if (perms.roles.includes('topManager')) return true;

    // Check specific ACL rule
    const key = `${module}:${subModule}`;
    const ops = perms.acl.get(key);
    if (ops && ops[operation]) return true;

    // Check wildcard rule (module:*)
    const wildcardKey = `${module}:*`;
    const wildcardOps = perms.acl.get(wildcardKey);
    if (wildcardOps && wildcardOps[operation]) return true;

    return false;
  }

  /** Check if current user has any read access to any subModule of a module */
  canAny(module: RbacModule): boolean {
    const perms = this.permissionsSubject.value;
    if (!perms) return false;
    if (perms.roles.includes('super_admin') || perms.roles.includes('admin') || perms.roles.includes('topManager')) return true;

    for (const [key, ops] of perms.acl.entries()) {
      if (key.startsWith(`${module}:`) && ops.read) return true;
    }
    return false;
  }

  /** Get current user's effective role names */
  getEffectiveRoles(): string[] {
    return this.permissionsSubject.value?.roles || [];
  }

  /** Get current user's effective groups */
  getEffectiveGroups(): string[] {
    return this.permissionsSubject.value?.groups || [];
  }

  /** Check if current user has a specific role */
  hasRole(roleName: string): boolean {
    const perms = this.permissionsSubject.value;
    if (!perms) return false;
    return perms.roles.includes(roleName);
  }

  get isLoaded(): boolean {
    return this.loaded;
  }

  // ══════════════════════════════════════════
  // HELPERS
  // ══════════════════════════════════════════

  private mapRole(id: string, data: Record<string, any>): RbacRole {
    return {
      id,
      name: data['name'] || '',
      label: data['label'] || data['name'] || '',
      description: data['description'] || '',
      isSystem: data['isSystem'] || false,
      createdAt: this.toDate(data['createdAt']) || new Date(),
      updatedAt: this.toDate(data['updatedAt']) || new Date(),
    };
  }

  private mapGroup(id: string, data: Record<string, any>): RbacGroup {
    return {
      id,
      name: data['name'] || '',
      description: data['description'] || '',
      memberIds: data['memberIds'] || [],
      roleIds: data['roleIds'] || [],
      managerId: data['managerId'] || '',
      managerName: data['managerName'] || '',
      location: data['location'] || '',
      department: data['department'] || '',
      parentGroupId: data['parentGroupId'] || undefined,
      isActive: data['isActive'] !== false,
      createdAt: this.toDate(data['createdAt']) || new Date(),
      updatedAt: this.toDate(data['updatedAt']) || new Date(),
    };
  }

  private mapAcl(id: string, data: Record<string, any>): RbacAclRule {
    return {
      id,
      module: data['module'] || 'hr',
      subModule: data['subModule'] || '*',
      operations: data['operations'] || { ...EMPTY_ACL_OPS },
      roleId: data['roleId'] || '',
      isActive: data['isActive'] !== false,
      createdAt: this.toDate(data['createdAt']) || new Date(),
      updatedAt: this.toDate(data['updatedAt']) || new Date(),
    };
  }

  private toDate(val: any): Date | undefined {
    if (!val) return undefined;
    if (val instanceof Date) return val;
    if (val?.toDate) return val.toDate();
    if (typeof val === 'string' || typeof val === 'number') return new Date(val);
    return undefined;
  }
}
